/**
 * DSP / WDSSO SSO HTTP calls. Cookie/session based; uses native fetch to stay
 * independent of the Cognito/CID request pipeline.
 */

import {
  getDspAuthenticateUrl,
  getDspSessionInfoUrl,
  getDspUserInfoUrl,
} from './config';
import {
  DSP_AUTH_API_VERSION,
  DSP_SESSION_API_VERSION,
  DSP_ID_HEADER,
  DSP_KEY_HEADER,
} from './constants';

const parseJsonSafe = async (response) => {
  let text = '';
  try {
    text = await response.text();
  } catch (e) {
    return {};
  }

  if (!text) {
    return {};
  }

  try {
    return JSON.parse(text);
  } catch (e) {
    return {};
  }
};

/**
 * Authenticate the user against WDSSO, establishing the AM SSO session.
 * The pair comes from the admin portal via getDspPair(); nothing is built in.
 */
export const dspAuthenticate = async (dsps, dspi) => {
  if (!dspi || !dsps) {
    return { ok: false, status: 0, data: { error: 'Missing dspi/dsps.' } };
  }

  try {
    const response = await fetch(getDspAuthenticateUrl(), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Accept-API-Version': DSP_AUTH_API_VERSION,
        'X-Requested-With': 'XMLHttpRequest',
        [DSP_ID_HEADER]: dspi,
        [DSP_KEY_HEADER]: dsps,
      },
      body: JSON.stringify({}),
    });

    return {
      ok: response.ok,
      status: response.status,
      data: await parseJsonSafe(response),
    };
  } catch (error) {
    return { ok: false, status: 0, data: { error: error && error.message } };
  }
};

/** Read the current DSP session info and resolve the authenticated username. */
export const getSessionInfo = async () => {
  try {
    const response = await fetch(getDspSessionInfoUrl(), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Accept-API-Version': DSP_SESSION_API_VERSION,
      },
      body: JSON.stringify({}),
    });

    const data = await parseJsonSafe(response);
    return {
      ok: response.ok,
      status: response.status,
      username: data.username || data.userId || '',
      data,
    };
  } catch (error) {
    return { ok: false, status: 0, username: '', data: { error: error && error.message } };
  }
};

/** Fetch the DSP staff user profile for a username. */
export const getUserInfo = async (username) => {
  if (!username) {
    return { ok: false, status: 0, data: {} };
  }

  try {
    const response = await fetch(getDspUserInfoUrl(username), {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return {
      ok: response.ok,
      status: response.status,
      data: await parseJsonSafe(response),
    };
  } catch (error) {
    return { ok: false, status: 0, data: { error: error && error.message } };
  }
};

export default {
  dspAuthenticate,
  getSessionInfo,
  getUserInfo,
};
